/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IsActiveMatchOptions } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { SettingsService } from 'app/core/services/settings/settings.service';
import { ZuperNavigationItem } from '@zuper/components/zuper-navigation/navigation.types';
import { ZuperVerticalNavigationComponent } from '../../vertical.component';
import { ZuperNavigationService } from '@zuper/components/zuper-navigation/navigation.service';
import { ZuperUtilsService } from '@zuper/services/utils';
import { AnalyticsService } from 'app/core/services/analytics/analytics.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';

@Component({
    selector       : 'zuper-vertical-navigation-basic-item',
    templateUrl    : './basic.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZuperVerticalNavigationBasicItemComponent implements OnInit, OnDestroy
{
    @Input() item: ZuperNavigationItem;
    @Input() name: string;

    isActiveMatchOptions: IsActiveMatchOptions;
    analyticKeys: any = {
        'dashboard': 'DASHBOARD',
        'dispatch_board': 'DISPATCH_BOARD',
        'products': 'PRODUCTS',
        'maps': 'MAPS',
        'settings': 'SETTINGS',
        'timesheet': 'TIMESHEET',
        'timeoff': 'TIMEOFF',
        'shifts': 'SHIFTS',
        'customers': 'CUSTOMERS',
        'organizations': 'ORGANIZATIONS',
        'properties': 'PROPERTIES',
        'jobs': 'JOBS',
        'chats': 'CHATS',
        'recurring_jobs': 'RECURRING_JOBS',
        'quotations': 'ESTIMATES',
        'invoices': 'INVOICES',
        'payments': 'PAYMENTS',
        'financing': 'FINANCING',
        'Reports': 'REPORTS',
        'Advanced Analytics': 'ADVANCED_ANALYTICS',
        'contracts': 'CONTRACTS',
        'assets': 'ASSETS',
        'zuperconnect': 'CONNECT',
    };

    private _zuperVerticalNavigationComponent: ZuperVerticalNavigationComponent;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _zuperNavigationService: ZuperNavigationService,
        private _zuperUtilsService: ZuperUtilsService,
        private _localStorageService: LocalStorageService,
        private settingsService: SettingsService,
        private _analytics: AnalyticsService,
        private _utilityService: UtilityService,
    )
    {
        // Set the equivalent of {exact: false} as default for active match options.
        // We are not assigning the item.isActiveMatchOptions directly to the
        // [routerLinkActiveOptions] because if it's "undefined" initially, the router
        // will throw an error and stop working.
        this.isActiveMatchOptions = this._zuperUtilsService.subsetMatchOptions;
    }

    getStateKey(item?: any): string {
        return this.analyticKeys[item?.id];
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Set the "isActiveMatchOptions" either from item's
        // "isActiveMatchOptions" or the equivalent form of
        // item's "exactMatch" option
        this.isActiveMatchOptions =
            this.item.isActiveMatchOptions ?? this.item.exactMatch
                ? this._zuperUtilsService.exactMatchOptions
                : this._zuperUtilsService.subsetMatchOptions;

        // Get the parent navigation component
        this._zuperVerticalNavigationComponent = this._zuperNavigationService.getComponent(this.name);

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Subscribe to onRefreshed on the navigation component
        this._zuperVerticalNavigationComponent.onRefreshed.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe(() => {

            // Mark for check
            this._changeDetectorRef.markForCheck();
        });
    }

    // On item with Link Clicked
    onItemLinkClicked(link: string): void{
        if(link === '/settings'){
            this._localStorageService.setLocalStorage('CurrentSettingsPage', link);
            this.settingsService.redirectSettings.next(true);
        } else if(link === '/calendar'){
            this._analytics.trackEvent('web_calendar_menu_button_clicked');}
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
